import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  adminService,
  type SiteContentPage,
} from "@/project/jain_elibrary-admin/services/adminService";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

type Props = {
  slug: string;
  heading: string;
};

export default function SiteContentEditor({ slug, heading }: Props) {
  const [page, setPage] = useState<SiteContentPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [isActive, setIsActive] = useState(true);

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "link",
    ],
    []
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const p = await adminService.getSiteContentPage(slug);
      setPage(p);
      setTitle(p.title);
      setBodyHtml(p.body_html ?? "");
      setIsActive(p.is_active);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } };
      if (err?.response?.status === 404) {
        toast.error(
          "This page is not in the database yet. Run Django migrations (e.g. 0030 for site pages, 0041 for privacy policy)."
        );
      } else {
        toast.error("Could not load page.");
      }
      setPage(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      await adminService.patchSiteContentPage(slug, {
        title: title.trim(),
        body_html: bodyHtml,
        is_active: isActive,
      });
      toast.success("Saved.");
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (!page) {
    return (
      <AdminLayout>
        <p className="text-muted-foreground">
          Page not available. Ensure API migrations ran and{" "}
          <code className="rounded bg-muted px-1">site_content_page</code> has
          slug <code className="rounded bg-muted px-1">{slug}</code>.
        </p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-foreground">{heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Edits publish to the public Jain eLibrary site (same API as the
            catalog).
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="page-title" required>
            Page title
          </Label>
          <Input
            id="page-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="active"
            checked={isActive}
            onCheckedChange={setIsActive}
          />
          <Label htmlFor="active">Published (visible on public site)</Label>
        </div>
        <div className="space-y-2">
          <Label required>Body</Label>
          <p className="text-xs text-muted-foreground">
            Press <strong>Enter</strong> to start a new paragraph. Add as many
            sections as you need using headings, lists, and bold from the
            toolbar.
          </p>
          <div className="cms-quill-wrap min-h-[360px] rounded-md border border-border bg-card">
            <ReactQuill
              theme="snow"
              value={bodyHtml}
              onChange={setBodyHtml}
              modules={quillModules}
              formats={quillFormats}
              className="[&_.ql-editor]:min-h-[300px]"
            />
          </div>
        </div>
        <Button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save
        </Button>
      </div>
    </AdminLayout>
  );
}
